import { Card, TURN_VALUE_TO_DIRECTION, type SubbookDefinition } from "./Card";
import { VariableState, OrbColor } from "./VariableState";
import { evaluateCondition, type ConditionContext } from "./Condition";
import { Familiar } from "@/game/entities/Familiar";
import { TileMap } from "@/game/map/TileMap";
import { Treasure } from "@/game/entities/Treasure";

/**
 * 実行状態
 */
export type ExecutionState = "idle" | "running" | "paused" | "finished";

/**
 * 実行結果
 */
export type ExecutionResult = "cleared" | "not_cleared" | "step_limit";

/**
 * 実行イベントリスナー
 *
 * cardId でカードを特定する（ネスト対応）。
 */
export interface ExecutionListener {
  onStepStart?: (cardId: string, card: Card) => void;
  onStepEnd?: (cardId: string, card: Card) => void;
  onFinish?: (result: ExecutionResult) => void;
  onStateChange?: (state: ExecutionState) => void;
  onVariableChange?: (color: OrbColor, value: number) => void;
}

/**
 * 実行設定
 */
export interface ExecutorConfig {
  stepDelay: number; // ステップ間の遅延（ms）
  maxSteps: number; // 最大ステップ数（無限ループ対策）
}

const DEFAULT_CONFIG: ExecutorConfig = {
  stepDelay: 500,
  maxSteps: 1000,
};

/** コールスタック深さの上限（ネストの深さ制限） */
const MAX_CALL_DEPTH = 10;

/**
 * 実行フレーム（コールスタックの1要素）
 */
interface ExecutionFrame {
  cards: Card[];
  index: number;
}

/**
 * プログラム実行エンジン
 *
 * スタックベースのツリーウォーキングインタプリタ。
 * if カードでは条件評価 → 真なら子カードのフレームを push。
 * フレーム完了で pop して親の続きへ。
 */
export class Executor {
  private familiar: Familiar;
  private tileMap: TileMap;
  private treasure: Treasure;
  private config: ExecutorConfig;
  private listener?: ExecutionListener;
  private variables: VariableState;

  private state: ExecutionState = "idle";
  private callStack: ExecutionFrame[] = [];
  private totalSteps = 0;
  private timeoutId?: ReturnType<typeof setTimeout>;
  private subbookDefinitions: SubbookDefinition[];

  constructor(
    familiar: Familiar,
    tileMap: TileMap,
    treasure: Treasure,
    config: Partial<ExecutorConfig> = {},
    subbookDefinitions: SubbookDefinition[] = []
  ) {
    this.familiar = familiar;
    this.tileMap = tileMap;
    this.treasure = treasure;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.variables = new VariableState();
    this.subbookDefinitions = subbookDefinitions;
  }

  /**
   * 変数の状態を取得
   */
  getVariables(): VariableState {
    return this.variables;
  }

  /**
   * イベントリスナーを設定
   */
  setListener(listener: ExecutionListener): void {
    this.listener = listener;
  }

  /**
   * 実行速度を設定
   */
  setStepDelay(delay: number): void {
    this.config.stepDelay = delay;
  }

  /**
   * 現在の状態を取得
   */
  getState(): ExecutionState {
    return this.state;
  }

  /**
   * プログラムを実行
   */
  async execute(cards: Card[]): Promise<ExecutionResult> {
    if (this.state === "running") {
      return "not_cleared";
    }

    this.setState("running");
    this.callStack = [{ cards, index: 0 }];
    this.totalSteps = 0;
    this.variables.reset();

    return new Promise((resolve) => {
      this.runStep(resolve);
    });
  }

  /**
   * 一時停止
   */
  pause(): void {
    if (this.state === "running") {
      this.setState("paused");
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = undefined;
      }
    }
  }

  /**
   * 再開
   */
  resume(resolve: (result: ExecutionResult) => void): void {
    if (this.state === "paused") {
      this.setState("running");
      this.runStep(resolve);
    }
  }

  /**
   * 停止
   */
  stop(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
    this.callStack = [];
    this.setState("idle");
  }

  /**
   * 1ステップ実行
   *
   * コールスタックの最上位フレームから次のカードを取得し実行する。
   * フレームのカードが尽きたらフレームをpopして親に戻る。
   * スタックが空になったら実行完了。
   */
  private runStep(resolve: (result: ExecutionResult) => void): void {
    // 一時停止中なら何もしない
    if (this.state === "paused") {
      return;
    }

    // ステップ上限チェック
    if (this.totalSteps >= this.config.maxSteps) {
      this.finish("step_limit", resolve);
      return;
    }

    // スタックが空 = 全カード実行完了
    if (this.callStack.length === 0) {
      const result = this.checkCleared() ? "cleared" : "not_cleared";
      this.finish(result, resolve);
      return;
    }

    const frame = this.callStack[this.callStack.length - 1];

    // 現フレームのカードが尽きた → フレームをpopして親に戻る
    if (frame.index >= frame.cards.length) {
      this.callStack.pop();
      this.runStep(resolve);
      return;
    }

    const card = frame.cards[frame.index];

    // if カード: branches を順に評価し、最初に真の分岐を実行
    if (card.type === "if") {
      this.listener?.onStepStart?.(card.id, card);
      this.totalSteps++;

      if (card.branches && card.branches.length > 0) {
        const ctx = this.createConditionContext();

        for (const branch of card.branches) {
          const conditionResult = branch.condition
            ? evaluateCondition(branch.condition, ctx)
            : true; // else は常に真

          if (conditionResult) {
            if (branch.cards.length > 0) {
              if (this.callStack.length >= MAX_CALL_DEPTH) {
                this.listener?.onStepEnd?.(card.id, card);
                this.finish("step_limit", resolve);
                return;
              }
              this.callStack.push({ cards: branch.cards, index: 0 });
            }
            break; // 最初に真の分岐のみ実行
          }
        }
      }

      frame.index++;

      this.timeoutId = setTimeout(() => {
        this.listener?.onStepEnd?.(card.id, card);
        this.runStep(resolve);
      }, this.config.stepDelay);
      return;
    }

    // while カード: 条件が真の間、bodyを繰り返し実行
    if (card.type === "while") {
      this.listener?.onStepStart?.(card.id, card);
      this.totalSteps++;

      if (card.branches && card.branches.length > 0) {
        const branch = card.branches[0];
        const ctx = this.createConditionContext();
        const conditionResult = branch.condition
          ? evaluateCondition(branch.condition, ctx)
          : true;

        if (conditionResult) {
          // 条件が真: body を実行（空 body でも index は進めず再評価。maxSteps で保護）
          if (branch.cards.length > 0) {
            if (this.callStack.length >= MAX_CALL_DEPTH) {
              this.listener?.onStepEnd?.(card.id, card);
              this.finish("step_limit", resolve);
              return;
            }
            this.callStack.push({ cards: branch.cards, index: 0 });
          }
          // frame.index を進めない → body 完了後（または空 body の次ステップで）再評価
        } else {
          // 条件が偽: ループを抜けて次のカードへ
          frame.index++;
        }
      } else {
        frame.index++;
      }

      this.timeoutId = setTimeout(() => {
        this.listener?.onStepEnd?.(card.id, card);
        this.runStep(resolve);
      }, this.config.stepDelay);
      return;
    }

    // call カード: サブブックのカード列をコールスタックにpush
    if (card.type === "call") {
      this.listener?.onStepStart?.(card.id, card);
      this.totalSteps++;

      const subbook = card.subbookId
        ? this.subbookDefinitions.find((d) => d.id === card.subbookId)
        : this.subbookDefinitions[0];
      if (subbook && subbook.cards.length > 0) {
        if (this.callStack.length >= MAX_CALL_DEPTH) {
          this.listener?.onStepEnd?.(card.id, card);
          this.finish("step_limit", resolve);
          return;
        }
        // 防御的コピー: 同じサブブックの複数回callで共有参照を避ける
        this.callStack.push({ cards: [...subbook.cards], index: 0 });
      }

      frame.index++;

      this.timeoutId = setTimeout(() => {
        this.listener?.onStepEnd?.(card.id, card);
        this.runStep(resolve);
      }, this.config.stepDelay);
      return;
    }

    // ステップ開始通知
    this.listener?.onStepStart?.(card.id, card);

    // 前進カードは1マスずつ実行
    if (card.type === "forward") {
      const rawSteps = card.orbColor
        ? this.variables.get(card.orbColor)
        : (card.value ?? 1);
      const steps = Number.isFinite(rawSteps) ? rawSteps : 0;
      if (steps <= 0) {
        this.totalSteps++;
        frame.index++;
        this.timeoutId = setTimeout(() => {
          this.listener?.onStepEnd?.(card.id, card);
          this.runStep(resolve);
        }, this.config.stepDelay);
        return;
      }
      this.runForwardSteps(card, steps, 0, resolve);
      return;
    }

    // その他のカードは即座に実行
    this.executeCard(card);
    this.totalSteps++;

    // クリア判定（ステップ途中でもクリア可能）
    if (this.checkCleared()) {
      this.listener?.onStepEnd?.(card.id, card);
      this.finish("cleared", resolve);
      return;
    }

    frame.index++;

    // ハイライトを stepDelay 中維持してから次のステップへ
    this.timeoutId = setTimeout(() => {
      this.listener?.onStepEnd?.(card.id, card);
      this.runStep(resolve);
    }, this.config.stepDelay);
  }

  /**
   * 前進カードを1マスずつ実行
   *
   * TODO: pause/resume 時に currentStep を復元できない制限あり。
   * 現在 pause 機能は未使用だが、将来対応が必要な場合は
   * pausedContext にサブステップ情報を保存する設計を検討する。
   */
  private runForwardSteps(
    card: Card,
    totalSteps: number,
    currentStep: number,
    resolve: (result: ExecutionResult) => void
  ): void {
    // 一時停止中なら何もしない
    if (this.state === "paused") {
      return;
    }

    // 全マス移動完了 or 壁に当たった場合
    const forward = this.familiar.getForwardPosition();
    const hitWall =
      currentStep < totalSteps &&
      !this.tileMap.isWalkable(forward.x, forward.y);

    if (currentStep >= totalSteps || hitWall) {
      this.completeForwardCard(card, resolve);
      return;
    }

    // 1マス前進
    this.familiar.moveTo(forward.x, forward.y);

    // 道中クリア判定
    if (this.checkCleared()) {
      this.totalSteps++;
      this.listener?.onStepEnd?.(card.id, card);
      this.finish("cleared", resolve);
      return;
    }

    // 次の1マスへ（遅延付き）
    this.timeoutId = setTimeout(() => {
      this.runForwardSteps(card, totalSteps, currentStep + 1, resolve);
    }, this.config.stepDelay);
  }

  /**
   * 前進カードの実行を完了し、次のカードへ進む
   */
  private completeForwardCard(
    card: Card,
    resolve: (result: ExecutionResult) => void
  ): void {
    this.totalSteps++;
    this.listener?.onStepEnd?.(card.id, card);

    if (this.callStack.length > 0) {
      this.callStack[this.callStack.length - 1].index++;
    }

    this.timeoutId = setTimeout(() => {
      this.runStep(resolve);
    }, this.config.stepDelay);
  }

  /**
   * カードを実行（前進・if以外）
   */
  private executeCard(card: Card): void {
    switch (card.type) {
      case "turn": {
        const direction = TURN_VALUE_TO_DIRECTION[card.value ?? 1];
        if (direction) this.familiar.turn(direction);
        break;
      }
      case "addToOrb":
        if (card.orbColor && card.value !== undefined) {
          this.variables.add(card.orbColor, card.value);
          this.listener?.onVariableChange?.(
            card.orbColor,
            this.variables.get(card.orbColor)
          );
        }
        break;
      case "multiplyOrb":
        if (card.orbColor && card.value !== undefined) {
          this.variables.multiply(card.orbColor, card.value);
          this.listener?.onVariableChange?.(
            card.orbColor,
            this.variables.get(card.orbColor)
          );
        }
        break;
      default:
        // forward, if, while, call は runStep で個別処理済み
        break;
    }
  }

  /**
   * 条件評価用のコンテキストを作成
   */
  private createConditionContext(): ConditionContext {
    return {
      forwardPosition: this.familiar.getForwardPosition(),
      isWalkable: (x, y) => this.tileMap.isWalkable(x, y),
      getOrbValue: (color) => this.variables.get(color),
    };
  }

  /**
   * クリア判定
   */
  private checkCleared(): boolean {
    return this.treasure.isAt(this.familiar.gridX, this.familiar.gridY);
  }

  /**
   * 実行終了
   */
  private finish(
    result: ExecutionResult,
    resolve: (result: ExecutionResult) => void
  ): void {
    this.callStack = [];
    this.setState("finished");
    this.listener?.onFinish?.(result);
    resolve(result);
  }

  /**
   * 状態を変更
   */
  private setState(state: ExecutionState): void {
    this.state = state;
    this.listener?.onStateChange?.(state);
  }
}
